import type { TranslationValues } from "next-intl";
import { BadgeError } from "@/components/badges/badge-error";
import { defaultCurrency, defaultCurrencyFormat } from "@/i18n/defaults";
import { getLocalePrice } from "@/lib/utilities";
import { cn } from "@/lib/utils";
import type { KeyValuePairProps } from "../components/pricing/price-element";
import type { DiscountType } from "../types/discount.type";

function getGroupedDiscounts(discounts?: DiscountType[]): DiscountType[] {
  if (!discounts) return [];

  const groupedDiscounts: DiscountType[] = [];

  discounts.forEach((discount) => {
    const existingDiscount = groupedDiscounts.find(
      (grouped) => discount.name === grouped.name,
    );

    if (existingDiscount) {
      existingDiscount.amount.gross += discount.amount.gross;
      existingDiscount.amount.net += discount.amount.net;
      existingDiscount.amount.tax += discount.amount.tax;
    } else {
      groupedDiscounts.push({
        name: discount.name,
        percentage: discount.percentage,
        type: discount.type,
        unit: discount.unit,
        amount: {
          gross: discount.amount.gross,
          net: discount.amount.net,
          tax: discount.amount.tax,
        },
      });
    }
  });
  return groupedDiscounts;
}

export const DiscountsKV = ({
  discounts,
  t,
  className,
  valueClassName,
}: {
  discounts?: DiscountType[];
  t: (key: string, values?: TranslationValues) => string;
  className?: string;
  valueClassName?: string;
}): KeyValuePairProps[] => {
  const discountsGrouped = getGroupedDiscounts(discounts);
  return (
    discountsGrouped?.map((discount) => ({
      label: discount.name,
      value: getLocalePrice({
        price: discount.amount.gross ?? 0,
      }),
      valueClassName: cn("text-brand-red font-bold", valueClassName),
      className: cn("font-normal", className),
    })) || []
  );
};

/**
 * @description Returns a list of discounts with the same name grouped together
 * @param discounts - The discounts to group
 * @param t - The translation function
 * @param className - The class name for the container
 * @param valueClassName - The class name for the value
 * @param total - Whether to show the total discount
 * @param type - The type of discount to sum and show
 * @param label - The label to show for the discount
 * @returns A list of discounts with the same name grouped together
 */
export const DiscountsKVParsed = ({
  discounts,
  t,
  className,
  valueClassName,
  total = false,
  type,
  label,
}: {
  discounts?: DiscountType[];
  t: (key: string, values?: TranslationValues) => string;
  className?: string;
  valueClassName?: string;
  total?: boolean;
  label?: string;
  type: DiscountType["type"][];
}): KeyValuePairProps[] | null => {
  let discountsGrouped = getGroupedDiscounts(discounts);

  if (type) {
    discountsGrouped = discountsGrouped.filter((d) => type.includes(d.type));
  }

  // Return null if no discounts
  if (!discountsGrouped || discountsGrouped.length === 0) {
    return null;
  }

  // Calculate total sum of all discount amounts
  const totalDiscountAmount =
    discountsGrouped?.reduce(
      (sum, discount) => sum + discount.amount.gross,
      0,
    ) || 0;

  const getLabel = (): string | undefined => {
    if (label) {
      return label === "codeName" ? discountsGrouped[0]!.name : label;
    }

    if (type?.includes("diet_length")) {
      return discountsGrouped[0]?.name;
    }

    return t("cart.configuration.discount.label");
  };

  return [
    {
      label: (
        <div>
          {getLabel()}
          {discountsGrouped?.some(
            (discount) => discount.unit === "percentage",
          ) &&
            !total && (
              <BadgeError className="ml-2">
                -{discountsGrouped?.map((d) => d.percentage).join(", ")}%
              </BadgeError>
            )}
        </div>
      ),
      value: `-${getLocalePrice({
        price: totalDiscountAmount,
      })}`,
      valueClassName: cn("text-brand-red font-bold", valueClassName),
      className: cn("font-normal", className),
    },
  ];
};
